import { EnumVersioneSezioneDTOTipoCompilazione } from '@framework/api-client/api/inquiries/schemas/enumVersioneSezioneDTOTipoCompilazione.ts';
import { type ModuloSezioneDTO } from '@framework/api-client/api/inquiries/schemas/moduloSezioneDTO.ts';
import { type ValoriSezione } from '@framework/api-client/api/inquiries-compile/schemas/valoriSezione.ts';
import {
  computeSectionShared,
  type IEngineSection,
} from '@framework/engine/schema/sections/IEngineSection.ts';
import { type IRootWidget } from '@framework/engine/schema/widgets/repository/containers/root/IRootWidget.ts';
import { applyTreeTranslationKeys } from '@framework/engine/schema/widgets/repository/translations/applyTreeTranslationKeys.ts';
import { produce } from 'immer';
import { useMemo } from 'react';
import { sectionMigrationEngine } from '../../../engine/migration/SectionMigrationEngine.ts';
import { type IFrontOfficeModule } from '../IFrontOfficeModule.ts';

interface IMapSectionSharedContext {
  requestModuleId: string | null | undefined;
  values: Pick<ValoriSezione, 'idSezione' | 'idModuloRichiestaSorgente'>[];
}

/**
 * Hook che ritorna una versione strutturata delle sezioni
 */
export function useSections(input: IFrontOfficeModule | null | undefined) {
  const sections = useMemo(() => {
    const sharedContext: IMapSectionSharedContext = {
      requestModuleId: input?.moduloRichiestaId,
      values: input?.valori ?? [],
    };

    return (
      (input?.sezioni
        ?.map(section => mapFrontofficeSectionToEngine(section, sharedContext))
        .filter(Boolean) as IEngineSection[]) ?? []
    );
  }, [input]);

  const values = useMemo<ValoriSezione[]>(() => input?.valori ?? [], [input]);

  return { sections, values };
}

/**
 * Mappiamo il ModuloSezione dal formato ottenuto dalle API a una IEngineSection,
 * utilizzato dal motore di validazione interno.
 *
 * Calcola anche il campo `shared` per classificare le sezioni condivise
 * (`PER_DOMANDA`) come `owned` o `external`.
 */
export function mapFrontofficeSectionToEngine(
  section: ModuloSezioneDTO,
  sharedContext?: IMapSectionSharedContext,
) {
  if (!section.sezione?.id) return null;

  const originalRoot = section.sezione?.ultimaVersione?.elementi as IRootWidget;
  const traduzioni = section.sezione.ultimaVersione?.traduzioni;

  const needsMigration = sectionMigrationEngine.needMigration(originalRoot);

  // Esegue migrazioni pendenti e applica traduzioni in un unico
  // produce(). L'uso di immer garantisce structural sharing:
  // solo i nodi mutati vengono clonati → compatibilità con React.memo.
  const root =
    needsMigration || traduzioni
      ? produce(originalRoot, draft => {
          if (needsMigration) {
            sectionMigrationEngine.run(draft);
          }
          if (traduzioni) {
            applyTreeTranslationKeys(draft, traduzioni);
          }
        })
      : originalRoot;

  const item: IEngineSection = {
    id: section.sezione.id,
    ultimaVersione: {
      ...section.sezione.ultimaVersione,
      versionId: section.sezione.ultimaVersione?.idVersione ?? null,
      elementi: root,
      visibile: section.regoleAbilitazione?.visible,
      compilabile: section.regoleAbilitazione?.enabled,
    },
    shared: sharedContext
      ? computeSectionShared({
          isSharedSection:
            section.sezione.ultimaVersione?.tipoCompilazione ===
            EnumVersioneSezioneDTOTipoCompilazione.PER_DOMANDA,
          requestModuleId: sharedContext.requestModuleId,
          sourceModuleId: sharedContext.values.find(
            v => v.idSezione === section.sezione?.id,
          )?.idModuloRichiestaSorgente,
        })
      : null,
  };

  return item;
}

type IUseSections = ReturnType<typeof useSections>;
export type ISections = IUseSections['sections'];
